import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  generateFieldName,
  type ModelProperty,
  type PropertyType,
  type ViewConfigField,
  type ViewConfigTreeNode,
  type ViewQueryField,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { Button, Checkbox, Col, Form, InputNumber, message, Row, Select } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { Array, Option, pipe } from 'effect'
import { isNotNullish, isNullish } from 'effect/Predicate'
import { t, type TFunction } from 'i18next'
import { type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { RSheetColumn } from '../../../../../domain/column'
import { type SearchResultRow, useSearchQueue } from '../../../../../recoil/hooks/searchRelation'
import { useSheetColumns } from '../../../../../recoil/selectors/useSheetColumns'
import { useGetModelProperty } from '../../../../../recoil/view/selectors/connectionsSelector'
import { useSetFieldDefaultValues, useViewConfigSheet } from '../../../../../recoil/viewConfig'
import { FieldDefaultValueDateInput } from './FieldDefaultValueDateInput'

export const FieldDefaultValuesForm = ({
  node,
  onAfterFinish,
  compiledConfig,
  query,
  setHasChangeToViewSchema,
}: {
  node: ViewConfigTreeNode
  onAfterFinish: () => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const config = useViewConfigSheet()
  const columns = useSheetColumns(compiledConfig, query)
  const getModelProperty = useGetModelProperty()
  const setFieldDefaultValues = useSetFieldDefaultValues()
  const translation = useTranslation()
  const [fields, setFields] = useState(() =>
    (config.fields ?? [])
      .map((field) => {
        const property = getModelProperty(field.property.modelName, field.property.propertyName)
        if (property === undefined) {
          // eslint-disable-next-line array-callback-return
          return
        }
        return {
          field,
          property,
        }
      })
      .filter(isNotNullish)
      .filter(
        ({ field, property }) =>
          field.property.nodeName === node.name && isNotNullish(property.write) && property.creatable !== false,
      ),
  )
  const onFinish = (): void => {
    setFieldDefaultValues({ fieldsWithDefaultValue: fields.map((x) => x.field) })
    setHasChangeToViewSchema(true)
    void message.info(t(`デフォルト値を更新しました`))
    onAfterFinish()
  }
  return (
    <Form layout="vertical" onFinish={onFinish}>
      <Row gutter={16}>
        {fields.map(({ field, property }, index) => {
          // XXX: columnsとfieldsが対応するかどうか保証がないが、現状のロジック上は一致するはずなのでこうしている。
          const column = columns.find((column) => column.field.name === generateFieldName(field.property))
          const updateFieldMeta = (metaPatch: Partial<ViewQueryField['meta']>): void => {
            setFields((fields) => {
              const newField: ViewConfigField = {
                ...field,
                override: {
                  ...field.override,
                  queryField: {
                    ...field.override?.queryField,
                    meta: { ...field.override?.queryField?.meta, ...metaPatch },
                  },
                },
              }
              return pipe(
                Array.replace(fields, index, { field: newField, property }),
                Option.getOrElse(() => fields),
              )
            })
          }
          const tooltip = buildDefaultValueTooltip(property.type, translation.t)
          return (
            <Col key={`${property.name}_${index}`} span={8}>
              <Form.Item label={field.override?.uiColumn?.label ?? property.label} tooltip={tooltip}>
                <FieldDefaultValueInput
                  field={field}
                  column={column}
                  property={property}
                  setDefaultValue={(defaultValue) => {
                    // 入力欄を空にしたときは空文字が渡るので、未設定として扱う
                    const isEmpty = isNullish(defaultValue) || defaultValue === ''
                    updateFieldMeta({ defaultValue: isEmpty ? undefined : defaultValue })
                  }}
                />
              </Form.Item>
            </Col>
          )
        })}
      </Row>
      <Row justify="end">
        <Form.Item>
          <Button htmlType="submit">{t(`適用`)}</Button>
        </Form.Item>
      </Row>
    </Form>
  )
}

/**
 * 項目名の右に出す補足を、項目の型に応じて返す
 * @param propertyType 項目の型
 * @param t 言語の切り替えに追従させるため、コンポーネントが持つ翻訳関数を受け取る
 * @returns 補足の文言。補足が無い型は undefined
 */
const buildDefaultValueTooltip = (propertyType: PropertyType, t: TFunction): string | undefined => {
  switch (propertyType) {
    case 'date':
    case 'datetime': {
      return t('レコード作成時点からの相対値が入ります')
    }
    default: {
      return undefined
    }
  }
}

type SetDefaultValue = (defaultValue: string | number | boolean | undefined) => void

interface Argument {
  field: ViewConfigField
  property: ModelProperty
  column: RSheetColumn<ViewQueryRecordNode> | undefined
  setDefaultValue: SetDefaultValue
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function FieldDefaultValueInput(argument: Argument): ReactElement {
  const { property } = argument
  switch (property.meta) {
    // case 'record_url':
    //   return <RecordUrlInput {...arg} />
    case 'relation': {
      return <ReferenceInput {...argument} />
    }
    // case 'url':
    //   return <UrlInput {...arg} />
    // case 'phone_number':
    //   return <UrlInput {...arg} />
    // case 'email':
    //   return <UrlInput {...arg} />
    // case 'html':
    //   return <HtmlInput {...arg} />
    case 'text': {
      return <TextInput {...argument} />
    }
    default: {
      break
    }
  }

  if ((property.selectOptions ?? []).length > 0) {
    // if (field.meta.fieldMetaType === 'multi_select') {
    //   return <MultiSelectOptionInput {...arg} />
    // }
    return <SelectOptionInput {...argument} />
  }

  switch (property.type) {
    case 'integer' as const: {
      return <NumberInput {...argument} />
    }
    case 'numeric' as const: {
      return <NumberInput {...argument} />
    }
    case 'date' as const:
    case 'datetime' as const: {
      return <FieldDefaultValueDateInput {...argument} />
    }
    case 'time' as const: {
      return <NotSupportedTypeInput />
    }
    case 'boolean' as const: {
      return <BooleanInput {...argument} />
    }
    // system系
    // case 'checkbox' as const:
    //   return <BooleanInput {...arg} />
    // case 'open' as const:
    //   return <OpenInput {...arg} />
    // case 'rowIndex' as const:
    //   return <span className="rsheet-cell-body-imvalid-row-index" />
    default: {
      // const x: never = column.type
      return <TextInput {...argument} />
    }
  }
}

const TextInput = ({ field, setDefaultValue, property }: Argument): ReactElement => {
  useEffect(() => {
    if (
      field.override?.queryField?.meta?.defaultValue !== undefined &&
      typeof field.override.queryField.meta.defaultValue !== 'string'
    ) {
      setDefaultValue(undefined)
    }
  }, [])

  return (
    <TextArea
      rows={property.meta === 'text' ? 2 : 1}
      allowClear
      value={
        typeof field.override?.queryField?.meta?.defaultValue === 'string'
          ? field.override.queryField.meta.defaultValue
          : undefined
      }
      placeholder={t(`デフォルト値なし`)}
      onChange={(e) => {
        const { value } = e.target
        setDefaultValue(value)
      }}
    />
  )
}

const NumberInput = ({ field, setDefaultValue }: Argument): ReactElement => {
  useEffect(() => {
    if (
      field.override?.queryField?.meta?.defaultValue !== undefined &&
      typeof field.override.queryField.meta.defaultValue !== 'number'
    ) {
      setDefaultValue(undefined)
    }
  }, [])
  return (
    <InputNumber
      style={{ width: '100%' }}
      value={
        typeof field.override?.queryField?.meta?.defaultValue === 'number'
          ? field.override.queryField.meta.defaultValue
          : undefined
      }
      placeholder={t(`デフォルト値なし`)}
      onChange={(value) => {
        setDefaultValue(value ?? undefined)
      }}
    />
  )
}

const BooleanInput = ({ field, setDefaultValue }: Argument): ReactElement => {
  useEffect(() => {
    if (
      field.override?.queryField?.meta?.defaultValue !== undefined &&
      typeof field.override.queryField.meta.defaultValue !== 'boolean'
    ) {
      setDefaultValue(undefined)
    }
  }, [])

  return (
    <Checkbox
      value={
        typeof field.override?.queryField?.meta?.defaultValue === 'boolean'
          ? field.override.queryField.meta.defaultValue
          : undefined
      }
      checked={
        typeof field.override?.queryField?.meta?.defaultValue === 'boolean'
          ? field.override.queryField.meta.defaultValue
          : undefined
      }
      onChange={(e) => {
        setDefaultValue(e.target.checked)
      }}
    />
  )
}

const NotSupportedTypeInput = (): ReactElement => (
  <div className="text-xs text-gray-400">{t(`現在サポートされていません`)}</div>
)

const SelectOptionInput = ({ field, property, setDefaultValue }: Argument): ReactElement => {
  const options = property.selectOptions ?? []

  useEffect(() => {
    const selectedOption = options.find((option) => option.value === field.override?.queryField?.meta?.defaultValue)
    if (field.override?.queryField?.meta?.defaultValue !== undefined && selectedOption === undefined) {
      setDefaultValue(undefined)
    }
  }, [])
  return (
    <Select
      allowClear
      placeholder={t(`デフォルト値なし`)}
      options={options}
      value={field.override?.queryField?.meta?.defaultValue}
      onChange={(value) => {
        setDefaultValue(value)
      }}
    />
  )
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO
type SearchResultRowWithKey = SearchResultRow & {
  key: string
}

const ReferenceInput = (argument: Argument): ReactElement => {
  const { field, column, setDefaultValue } = argument
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SearchResultRowWithKey[]>([])
  const { queueSearch, search } = useSearchQueue({
    setLoading,
    setOptions: (options) => {
      setOptions(options.map((x) => ({ ...x, key: x.value })))
    },
  })

  const searchSql = column?.field?.read.searchSql ?? column?.node.read.join?.search.sql

  useEffect(() => {
    if (options.length === 0 && searchSql !== undefined) {
      void search(searchSql, '')
    }
  }, [])

  if (searchSql === undefined) {
    return <TextInput {...argument} />
  }

  return (
    <Select
      loading={loading}
      allowClear
      placeholder={t(`デフォルト値なし`)}
      showSearch
      filterOption={false}
      onChange={(value) => {
        setDefaultValue(value)
      }}
      onSearch={(searchKey) => {
        queueSearch(searchSql, searchKey)
      }}
      value={field.override?.queryField?.meta?.defaultValue}
    >
      {options.map((option) => (
        <Select.Option key={option.key} value={option.value}>
          {option.label}
          <span className="ml-2 text-gray-400">
            {option.meta_label === undefined ? '' : '- '}
            {option.meta_label}
          </span>
        </Select.Option>
      ))}
    </Select>
  )
}
